import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  MessageSquare, Mail, Smartphone, Bell, Send, RefreshCw,
  CheckCircle, XCircle, Clock, AlertTriangle, Activity,
  Users, Settings, Search, Filter, TrendingUp, Zap,
  Phone, Radio, AlertCircle
} from 'lucide-react';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { staffLabel, STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

// ═══════════════════════════════════════════════════════════════
// COMMUNICATION HUB PAGE
// Section 8: Unified Communication System
// ═══════════════════════════════════════════════════════════════

export default function CommunicationHubPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [testSmsDialogOpen, setTestSmsDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Filters
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get('/communication-hub/dashboard');
      setDashboard(res.data.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      toast.error('Failed to load communication dashboard');
    }
  }, []);

  // Load communication logs
  const loadLogs = useCallback(async () => {
    try {
      const params = { days: 30, limit: 200 };
      if (channelFilter !== 'all') params.channel = channelFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const res = await api.get('/communication-hub/logs', { params });
      setLogs(res.data.data || []);
    } catch (err) {
      console.error('Failed to load logs:', err);
    }
  }, [channelFilter, statusFilter]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadLogs()]);
      setLoading(false);
    };
    loadAll();
  }, [loadDashboard, loadLogs]);

  useEffect(() => {
    loadLogs();
  }, [channelFilter, statusFilter, loadLogs]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([loadDashboard(), loadLogs()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  // Filter logs by search
  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      log.recipient_name?.toLowerCase().includes(q) ||
      log.title?.toLowerCase().includes(q) ||
      log.message?.toLowerCase().includes(q) ||
      log.notification_type?.toLowerCase().includes(q)
    );
  });

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="communication-hub-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Communication Hub</h1>
          <p className="text-muted-foreground">Unified notifications &amp; staff alerts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)} data-testid="settings-btn">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button size="sm" onClick={() => setSendDialogOpen(true)} data-testid="send-notification-btn">
            <Send className="h-4 w-4 mr-2" />
            Send Notification
          </Button>
        </div>
      </div>

      {/* SMS Configuration Alert */}
      {dashboard?.alerts?.sms_not_configured && (
        <Alert variant="warning" className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>SMS Not Configured:</strong> Twilio credentials are missing. SMS notifications are being logged but not sent.
            Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_FROM_NUMBER to enable live SMS.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <Activity className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <MessageSquare className="h-4 w-4 mr-2" />
            Message Logs
          </TabsTrigger>
          <TabsTrigger value="shift-alerts" data-testid="tab-shift-alerts">
            <Zap className="h-4 w-4 mr-2" />
            Shift Alerts
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Channel Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ChannelStatusCard
              title="SMS"
              icon={Smartphone}
              status={dashboard?.channels?.SMS}
              stats={dashboard?.logs_summary?.by_channel?.SMS}
            />
            <ChannelStatusCard
              title="Email"
              icon={Mail}
              status={dashboard?.channels?.EMAIL}
              stats={dashboard?.logs_summary?.by_channel?.EMAIL}
            />
            <ChannelStatusCard
              title="In-App"
              icon={Bell}
              status={dashboard?.channels?.IN_APP}
              stats={dashboard?.logs_summary?.by_channel?.IN_APP}
            />
            <ChannelStatusCard
              title="Push"
              icon={Radio}
              status={dashboard?.channels?.PUSH}
              stats={{}}
            />
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Sent (30 days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">
                  {dashboard?.logs_summary?.totals?.sent || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboard?.logs_summary?.totals?.success_rate || 100}% success rate
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Failed (30 days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-destructive">
                  {dashboard?.logs_summary?.totals?.failed || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboard?.alerts?.failed_last_24h || 0} in last 24h
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {dashboard?.recent_activity?.length || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  notification types active
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          {dashboard?.recent_activity?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Activity (24h)</CardTitle>
                <CardDescription>Notification types sent in the last 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {dashboard.recent_activity.map((activity, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {activity.notification_type?.replace(/_/g, ' ')} ({activity.count})
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="search-logs"
                    />
                  </div>
                </div>
                <Select value={channelFilter} onValueChange={setChannelFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="channel-filter">
                    <SelectValue placeholder="Channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    <SelectItem value="SMS">SMS</SelectItem>
                    <SelectItem value="EMAIL">Email</SelectItem>
                    <SelectItem value="IN_APP">In-App</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="SENT">Sent</SelectItem>
                    <SelectItem value="DELIVERED">Delivered</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No communication logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.slice(0, 50).map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{log.recipient_name}</p>
                            <p className="text-xs text-muted-foreground">{log.recipient_contact}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ChannelBadge channel={log.channel} />
                        </TableCell>
                        <TableCell>
                          <span className="text-xs">{log.notification_type?.replace(/_/g, ' ')}</span>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            <p className="text-sm font-medium truncate">{log.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{log.message}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={log.status} error={log.error_message} />
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">
                            {log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shift Alerts Tab */}
        <TabsContent value="shift-alerts" className="space-y-4">
          <ShiftAlertsPanel onRefresh={loadLogs} />
        </TabsContent>
      </Tabs>

      {/* Send Notification Dialog */}
      <SendNotificationDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        onSuccess={() => {
          loadLogs();
          loadDashboard();
        }}
      />

      {/* Settings Dialog */}
      <CommunicationSettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

function ChannelStatusCard({ title, icon: Icon, status, stats }) {
  const isActive = status?.status === 'ACTIVE';
  const isMock = status?.status === 'MOCK';
  const isNotConfigured = status?.status === 'NOT_CONFIGURED' || status?.status === 'NOT_IMPLEMENTED';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
          {isActive && <Badge className="bg-emerald-500 text-white">Active</Badge>}
          {isMock && <Badge variant="outline" className="border-amber-500 text-amber-600">Mock</Badge>}
          {isNotConfigured && <Badge variant="secondary">Disabled</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Provider</span>
            <span className="font-medium">{status?.provider || 'N/A'}</span>
          </div>
          {stats && (
            <>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sent</span>
                <span className="font-medium text-emerald-600">{stats.SENT || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Failed</span>
                <span className="font-medium text-destructive">{stats.FAILED || 0}</span>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ChannelBadge({ channel }) {
  const config = {
    SMS: { icon: Smartphone, color: 'bg-blue-500' },
    EMAIL: { icon: Mail, color: 'bg-purple-500' },
    IN_APP: { icon: Bell, color: 'bg-emerald-500' },
    PUSH: { icon: Radio, color: 'bg-orange-500' },
  };
  const { icon: Icon, color } = config[channel] || { icon: MessageSquare, color: 'bg-gray-500' };

  return (
    <Badge className={`${color} text-white text-xs`}>
      <Icon className="h-3 w-3 mr-1" />
      {channel}
    </Badge>
  );
}

function StatusBadge({ status, error }) {
  const config = {
    SENT: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    DELIVERED: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    FAILED: { icon: XCircle, color: 'text-destructive', bg: 'bg-destructive/10' },
    PENDING: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100' },
    READ: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-100' },
  };
  const { icon: Icon, color, bg } = config[status] || { icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100' };

  return (
    <div className="flex items-center gap-1">
      <Badge variant="outline" className={`${bg} ${color} border-0 text-xs`}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
      {error && (
        <span className="text-xs text-destructive" title={error}>
          <AlertCircle className="h-3 w-3" />
        </span>
      )}
    </div>
  );
}

function ShiftAlertsPanel({ onRefresh }) {
  const [sending, setSending] = useState(false);
  const [hoursAhead, setHoursAhead] = useState('24');
  const [result, setResult] = useState(null);

  const handleSendReminders = async () => {
    setSending(true);
    try {
      const res = await api.post(`/communication-hub/shift-reminders?hours_ahead=${hoursAhead}`);
      setResult(res.data.data);
      toast.success(`Sent ${res.data.data.reminders_sent} reminders`);
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to send shift reminders');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Send Shift Reminders</CardTitle>
          <CardDescription>
            Automatically send reminders to staff with upcoming shifts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="hours-ahead">Hours Ahead</Label>
              <Select value={hoursAhead} onValueChange={setHoursAhead}>
                <SelectTrigger id="hours-ahead" className="w-full" data-testid="hours-ahead-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="24">24 hours (Tomorrow)</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                  <SelectItem value="72">72 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="pt-6">
              <Button onClick={handleSendReminders} disabled={sending} data-testid="send-reminders-btn">
                {sending ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Reminders
              </Button>
            </div>
          </div>

          {result && (
            <Alert className="border-emerald-500/50 bg-emerald-500/10">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800">
                <strong>Reminders Sent!</strong><br />
                Target Date: {result.target_date}<br />
                Shifts Found: {result.shifts_found}<br />
                Sent: {result.reminders_sent} | Failed: {result.reminders_failed}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Shift Alert Types</CardTitle>
          <CardDescription>
            Alerts are automatically triggered by the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AlertTypeCard
              title="Shift Assigned"
              description="Sent when a new shift is assigned to staff"
              icon={Users}
              channels={['IN_APP', 'SMS']}
            />
            <AlertTypeCard
              title="Shift Reminder"
              description="Sent 24h before shift starts"
              icon={Clock}
              channels={['IN_APP', 'SMS']}
            />
            <AlertTypeCard
              title="Shift Cancelled"
              description="Urgent notification when shift is cancelled"
              icon={XCircle}
              channels={['IN_APP', 'SMS']}
            />
            <AlertTypeCard
              title="Check-In Reminder"
              description="Sent 15 minutes before shift"
              icon={Activity}
              channels={['IN_APP', 'SMS']}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AlertTypeCard({ title, description, icon: Icon, channels }) {
  return (
    <div className="p-4 border rounded-lg">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-sm">{title}</h4>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
          <div className="flex gap-1 mt-2">
            {channels.map(ch => (
              <Badge key={ch} variant="outline" className="text-[10px]">{ch}</Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SendNotificationDialog({ open, onOpenChange, onSuccess }) {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    recipient_user_id: '',
    notification_type: 'CUSTOM',
    title: '',
    message: '',
    channels: ['IN_APP'],
    priority: 'NORMAL',
  });

  const handleChannelToggle = (channel) => {
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  const handleSend = async () => {
    if (!form.recipient_user_id || !form.title || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.channels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    setSending(true);
    try {
      await api.post('/communication-hub/send', form);
      toast.success('Notification sent successfully');
      onOpenChange(false);
      onSuccess?.();
      setForm({
        recipient_user_id: '',
        notification_type: 'CUSTOM',
        title: '',
        message: '',
        channels: ['IN_APP'],
        priority: 'NORMAL',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send Notification</DialogTitle>
          <DialogDescription>
            Send a notification to a staff member via multiple channels
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Recipient *</Label>
            <AsyncSearchSelect
              endpoint="/staff"
              value={form.recipient_user_id}
              onChange={v => setForm({ ...form, recipient_user_id: v })}
              getOptionValue={(s) => s.user_id || s.id}
              getOptionLabel={staffLabel}
              placeholder="Select staff member"
              searchPlaceholder={STAFF_SEARCH_HINT}
              emptyText="No staff match that search"
              testId="recipient-select"
            />
          </div>

          <div>
            <Label>Title *</Label>
            <Input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Notification title"
              data-testid="notification-title"
            />
          </div>

          <div>
            <Label>Message *</Label>
            <Textarea
              value={form.message}
              onChange={e => setForm({ ...form, message: e.target.value })}
              placeholder="Enter your message..."
              rows={3}
              data-testid="notification-message"
            />
          </div>

          <div>
            <Label>Channels</Label>
            <div className="flex gap-4 mt-2">
              {['IN_APP', 'SMS', 'EMAIL'].map(channel => (
                <label key={channel} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.channels.includes(channel)}
                    onCheckedChange={() => handleChannelToggle(channel)}
                    data-testid={`channel-${channel.toLowerCase()}`}
                  />
                  <span className="text-sm">{channel.replace('_', '-')}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="NORMAL">Normal</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="URGENT">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending} data-testid="confirm-send-btn">
            {sending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommunicationSettingsDialog({ open, onOpenChange }) {
  const [settings, setSettings] = useState({
    sms_enabled: true,
    email_enabled: true,
    shift_reminders_enabled: true,
    reminder_hours_before: 24,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get('/communication-hub/status')
        .then(res => {
          const channels = res.data.data?.channels || {};
          setSettings({
            sms_enabled: channels.SMS?.enabled ?? true,
            email_enabled: channels.EMAIL?.enabled ?? true,
            shift_reminders_enabled: true,
            reminder_hours_before: 24,
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/communication-hub/settings', {
        sms_enabled: settings.sms_enabled,
        email_enabled: settings.email_enabled,
        shift_reminders_enabled: settings.shift_reminders_enabled,
        reminder_hours_before: settings.reminder_hours_before,
      });
      toast.success('Settings saved');
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Communication Settings</DialogTitle>
          <DialogDescription>
            Configure notification channels and automation
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">SMS Notifications</p>
                <p className="text-xs text-muted-foreground">Enable SMS alerts via Twilio</p>
              </div>
              <Checkbox
                checked={settings.sms_enabled}
                onCheckedChange={checked => setSettings({ ...settings, sms_enabled: checked })}
                data-testid="setting-sms-enabled"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Email Notifications</p>
                <p className="text-xs text-muted-foreground">Enable email alerts via Resend</p>
              </div>
              <Checkbox
                checked={settings.email_enabled}
                onCheckedChange={checked => setSettings({ ...settings, email_enabled: checked })}
                data-testid="setting-email-enabled"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Automatic Shift Reminders</p>
                <p className="text-xs text-muted-foreground">Send daily shift reminders</p>
              </div>
              <Checkbox
                checked={settings.shift_reminders_enabled}
                onCheckedChange={checked => setSettings({ ...settings, shift_reminders_enabled: checked })}
                data-testid="setting-reminders-enabled"
              />
            </div>

            <div>
              <Label>Reminder Hours Before Shift</Label>
              <Select
                value={String(settings.reminder_hours_before)}
                onValueChange={v => setSettings({ ...settings, reminder_hours_before: parseInt(v) })}
              >
                <SelectTrigger data-testid="reminder-hours-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="save-settings-btn">
            {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
